import type { RecommendationContextAxis } from "@memorang/types";
import type { FullCell } from "../contract/recommendation-context-types";
import { trackKey } from "./cell-model";

export const allocateDistinctFirst = ({
	rankedCandidates,
	sessionCount,
}: {
	rankedCandidates: FullCell[];
	sessionCount: number;
}): FullCell[] =>
	Array.from(
		{ length: sessionCount },
		(_, index) => rankedCandidates[index % rankedCandidates.length],
	);

const preferredTrackOrder = ({
	cell,
	trackAxes,
	preferredTrackValueIds,
}: {
	cell: FullCell;
	trackAxes: RecommendationContextAxis[];
	preferredTrackValueIds?: number[];
}): number => {
	if (!preferredTrackValueIds?.length || trackAxes.length === 0) {
		return Number.POSITIVE_INFINITY;
	}
	const index = preferredTrackValueIds.indexOf(
		cell.coordinates[trackAxes[0].id],
	);
	return index < 0 ? preferredTrackValueIds.length : index;
};

export const allocateOnePerTrack = ({
	rankedCandidates,
	trackAxes,
	sessionCount,
	preferredTrackValueIds,
}: {
	rankedCandidates: FullCell[];
	trackAxes: RecommendationContextAxis[];
	sessionCount: number;
	preferredTrackValueIds?: number[];
}): FullCell[] => {
	const selected: FullCell[] = [];
	const seen = new Set<string>();
	for (const cell of rankedCandidates) {
		if (selected.length >= sessionCount) {
			break;
		}
		const key = trackKey(cell, trackAxes);
		if (seen.has(key)) {
			continue;
		}
		seen.add(key);
		selected.push(cell);
	}
	return selected.toSorted(
		(left, right) =>
			preferredTrackOrder({
				cell: left,
				trackAxes,
				preferredTrackValueIds,
			}) -
			preferredTrackOrder({
				cell: right,
				trackAxes,
				preferredTrackValueIds,
			}),
	);
};

export const validateSessionRequest = ({
	itemsPerSession,
	durationMinutes,
	sessionCount,
}: {
	itemsPerSession?: number;
	durationMinutes?: 5 | 10 | 20;
	sessionCount: number;
}): void => {
	if ((itemsPerSession === undefined) === (durationMinutes === undefined)) {
		throw new RangeError(
			"provide exactly one of itemsPerSession or durationMinutes",
		);
	}
	if (
		itemsPerSession !== undefined &&
		(!Number.isInteger(itemsPerSession) ||
			itemsPerSession < 1 ||
			itemsPerSession > 50)
	) {
		throw new RangeError("itemsPerSession must be an integer between 1 and 50");
	}
	if (durationMinutes !== undefined && ![5, 10, 20].includes(durationMinutes)) {
		throw new RangeError("durationMinutes must be 5, 10, or 20");
	}
	if (
		!Number.isInteger(sessionCount) ||
		sessionCount < 1 ||
		sessionCount > 24
	) {
		throw new RangeError("sessionCount must be an integer between 1 and 24");
	}
};
